import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiTokenService } from '../services/apiTokenService';
import type { ApiToken, CreateApiTokenDTO, GeneratedApiToken } from '../types/apiToken.types';
import { useToast } from '@/shared/hooks/use-toast';
import { logger } from '@/infrastructure/lib/logger';
import { resolveApiErrorMessage, apiErrorTitle } from '@/shared/utils/apiErrorMessage';
import i18n from '@/app/i18n/config';

export function useApiTokensQuery(workspaceId: string) {
  return useQuery<ApiToken[]>({
    queryKey: ['api-tokens', workspaceId],
    queryFn: () => ApiTokenService.listByWorkspace(workspaceId),
    enabled: !!workspaceId
  });
}

export function useCreateToken(workspaceId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: CreateApiTokenDTO) => ApiTokenService.create(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['api-tokens', workspaceId] });
    },
    onError: (error: Error) => {
      logger.error('Failed to create api token', { error: error?.message });
      toast({
        variant: 'destructive',
        title: apiErrorTitle(),
        description: resolveApiErrorMessage(error)
      });
    }
  });
}

export function useRevokeToken(workspaceId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (tokenId: string) => ApiTokenService.revoke(tokenId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['api-tokens', workspaceId] });
      toast({ title: i18n.t('workspaces.apiTokens.revoked') });
    },
    onError: (error: Error) => {
      logger.error('Failed to revoke api token', { error: error?.message });
      toast({ variant: 'destructive', title: apiErrorTitle(), description: resolveApiErrorMessage(error) });
    }
  });
}

export function useRenewToken(workspaceId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => ApiTokenService.renew(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['api-tokens', workspaceId] });
      toast({ title: i18n.t('workspaces.apiTokens.renewed') });
    },
    onError: (error: Error) => {
      logger.error('Failed to renew token', { error: error?.message });
      toast({ variant: 'destructive', title: apiErrorTitle(), description: resolveApiErrorMessage(error) });
    }
  });
}

export function useRegenerateToken(
  workspaceId: string,
  tokens: ApiToken[],
  onSuccess: (token: GeneratedApiToken) => void
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (tokenId: string) => {
      const token = tokens.find(t => t.id === tokenId);
      if (!token) throw new Error('Token not found');

      await ApiTokenService.delete(tokenId);
      return ApiTokenService.create({
        product_id: workspaceId,
        token_name: token.token_name,
        expires_at: token.expires_at
      });
    },
    onSuccess: async (newToken) => {
      onSuccess(newToken);
      await queryClient.invalidateQueries({ queryKey: ['api-tokens', workspaceId] });
      toast({ title: i18n.t('workspaces.apiTokens.regenerated') });
    },
    onError: (error: Error) => {
      logger.error('Failed to regenerate token', { error: error?.message });
      toast({ variant: 'destructive', title: apiErrorTitle(), description: resolveApiErrorMessage(error) });
    }
  });
}
